/**
 * In-memory holders for the two MFA proofs. Kept in their own module (not React
 * state) so `apiRequest` can attach the headers without importing AuthContext
 * (which would create a cycle).
 *
 * They are NOT interchangeable — see `requireStepUpIfEnrolled` server-side:
 *   mfaToken  — "this session satisfied 2FA". A trusted device earns it at
 *               bootstrap, so it says nothing about who is at the keyboard now.
 *   sudoToken — "the user proved possession just now". Only a fresh challenge
 *               or enrollment mints it; short-lived; required to change 2FA
 *               settings. Never derive one from the other.
 *
 * Deliberately memory-only: persisting either to localStorage would let a
 * stolen browser profile replay it.
 */
let mfaToken: string | null = null;
let sudoToken: string | null = null;
let sudoExpiresAtMs = 0;
let onRequired: (() => void) | null = null;

export const mfaTokenStore = {
  get: (): string | null => mfaToken,
  set: (t: string | null): void => { mfaToken = t; },
  clear: (): void => { mfaToken = null; sudoToken = null; sudoExpiresAtMs = 0; },
  setOnRequired: (cb: (() => void) | null): void => { onRequired = cb; },
  triggerRequired: (): void => { try { onRequired?.(); } catch { /* noop */ } },

  /** Non-null only while the step-up window is genuinely still open. */
  getSudo: (): string | null => {
    if (!sudoToken || Date.now() >= sudoExpiresAtMs) return null;
    return sudoToken;
  },
  setSudo: (t: string, expiresAtMs: number): void => { sudoToken = t; sudoExpiresAtMs = expiresAtMs; },
  clearSudo: (): void => { sudoToken = null; sudoExpiresAtMs = 0; },
};
